// src/web/envMap.js
//
// Loads an environment map for image-based lighting and lens reflections.
//
// Loading priority (first one found wins):
//   1. Cubemap   /assets/env/{px,nx,py,ny,pz,nz}.png        ← preferred (your existing assets)
//   2. Cubemap   /assets/env/{px,nx,py,ny,pz,nz}.jpg
//   3. Cubemap   /assets/env/{posx,negx,posy,negy,posz,negz}.jpg   (alternate naming)
//   4. HDR       /assets/env/studio.hdr
//   5. HDR       /assets/env/default.hdr
//   6. Procedural RoomEnvironment (fallback)
//
// All formats are run through PMREMGenerator so they produce correctly
// pre-filtered roughness mipmaps for PBR materials. This is what makes the
// reflections look right on rough vs smooth lens surfaces.

import { PMREMGenerator, CubeTextureLoader, SRGBColorSpace } from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

const CUBE_SETS = [
  // [order matches Three.js CubeTextureLoader: +X, -X, +Y, -Y, +Z, -Z]
  { base: '/assets/env/', ext: 'png', files: ['px', 'nx', 'py', 'ny', 'pz', 'nz'] },
  { base: '/assets/env/', ext: 'jpg', files: ['px', 'nx', 'py', 'ny', 'pz', 'nz'] },
  { base: '/assets/env/', ext: 'jpg', files: ['posx', 'negx', 'posy', 'negy', 'posz', 'negz'] }
];

const HDR_CANDIDATES = [
  '/assets/env/studio.hdr',
  '/assets/env/default.hdr'
];

// HEAD probe to find which (if any) of the cubemap sets is actually present
// on the server. Avoids the noisy 404s of always trying CubeTextureLoader on
// missing files.
async function probe(url) {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    return res.ok;
  } catch {
    return false;
  }
}

async function tryLoadCubemap(set) {
  const urls = set.files.map(f => `${set.base}${f}.${set.ext}`);
  const allPresent = (await Promise.all(urls.map(probe))).every(Boolean);
  if (!allPresent) return null;

  return new Promise((resolve) => {
    const loader = new CubeTextureLoader();
    loader.load(
      urls,
      (cube) => {
        cube.colorSpace = SRGBColorSpace;
        resolve({ kind: 'cube', texture: cube, baseUrl: set.base, ext: set.ext });
      },
      undefined,
      () => resolve(null)
    );
  });
}

async function tryLoadHDR(url) {
  if (!(await probe(url))) return null;
  return new Promise((resolve) => {
    new RGBELoader().load(
      url,
      (tex) => resolve({ kind: 'hdr', texture: tex, url }),
      undefined,
      () => resolve(null)
    );
  });
}

export async function loadEnvironment(renderer) {
  const pmrem = new PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  pmrem.compileCubemapShader();

  // 1-3. Cubemap candidates
  for (const set of CUBE_SETS) {
    const result = await tryLoadCubemap(set);
    if (result) {
      const envMap = pmrem.fromCubemap(result.texture).texture;
      result.texture.dispose();
      pmrem.dispose();
      console.log(`[envMap] Loaded cubemap ${result.baseUrl}{${result.ext}}`);
      return envMap;
    }
  }

  // 4-5. HDR candidates
  for (const url of HDR_CANDIDATES) {
    const result = await tryLoadHDR(url);
    if (result) {
      const envMap = pmrem.fromEquirectangular(result.texture).texture;
      result.texture.dispose();
      pmrem.dispose();
      console.log('[envMap] Loaded HDR', url);
      return envMap;
    }
  }

  // 6. Procedural fallback
  console.warn('[envMap] No cubemap or HDR found in /assets/env/. Using procedural RoomEnvironment.');
  const envMap = pmrem.fromScene(new RoomEnvironment()).texture;
  pmrem.dispose();
  return envMap;
}
